// Content for the London Drumstick Co. case study (Boost! portfolio piece)
// Source of truth: the feature brief supplied by the client. Nothing invented
// beyond the listed features. Stat tiles are editable placeholders.

export const BOOST_LOGO =
  'https://customer-assets-lqy194kg.emergentagent.net/job_31bcde7c-d98a-4c27-a3ab-d96f08806001/artifacts/e3mvklhv_Logo%20Pink.png';

// Branded 1536×1024 emerald-and-gold social share card (Open Graph / Twitter)
export const LDS_OG_IMAGE =
  'https://static.prod-images.emergentagent.com/jobs/31bcde7c-d98a-4c27-a3ab-d96f08806001/images/c6626ba3f24272b410f08881695c6b9b63a36da928b32d29c637567ccff412da.jpeg';

export const LDS_HERO = {
  eyebrow: 'Boost! · Case Study',
  chips: ['E-commerce', 'Custom product configurator', 'CRM & support', 'Fulfilment automation', 'AI'],
  year: '2024–2025',
  title: 'London Drumstick Co.',
  positioning:
    'A direct-to-drummer store, custom stick builder and full back-office, built as one system.',
  client: 'London Drumstick Co.',
  liveUrl: 'https://www.londondrumsticks.co.uk',
  // Real storefront homepage screenshot (desktop) supplied by the client
  image:
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/84zc1llp_Screenshot%202026-10-02%20142954.png',
};

// Sticky in-page section navigation
export const LDS_NAV = [
  { id: 'challenge', label: 'The brief' },
  { id: 'glance', label: 'At a glance' },
  { id: 'storefront', label: 'Storefront' },
  { id: 'builder', label: 'Stick Builder' },
  { id: 'loyalty', label: 'Loyalty & growth' },
  { id: 'artists', label: 'Artists' },
  { id: 'international', label: 'International' },
  { id: 'backoffice', label: 'Back-office' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'results', label: 'Results' },
];

export const LDS_CHALLENGE = {
  kicker: '01 — The brief',
  title: 'One system to run the whole business',
  lead:
    'A heritage drumstick brand selling standard sticks, custom-made sticks, accessories and merch — with an endorsed-artist programme and a small team behind it all.',
  copy: [
    'They needed a store that converts on mobile, a configurator for made-to-order sticks, and a back-office that replaces spreadsheets, inboxes and manual courier bookings — all in one place.',
    'The goal was deliberately ambitious: no monthly SaaS sprawl, no patchwork of disconnected tools. One platform the team actually enjoys running, that treats the shop and the operation as a single living system.',
  ],
  points: [
    { icon: 'Store', title: 'A shop that sells on a phone', copy: 'Most drummers browse on mobile — the storefront had to feel fast, premium and effortless to buy from.' },
    { icon: 'Wand2', title: 'Made-to-order, made simple', copy: 'Custom sticks are the signature product; configuring and pricing them had to be a creative experience, not a form.' },
    { icon: 'LayoutDashboard', title: 'Kill the spreadsheets', copy: 'Orders, fulfilment, courier bookings, customer emails and the artist roster all lived in scattered tools. They wanted one.' },
    { icon: 'Coins', title: 'No SaaS sprawl', copy: 'Every bit of functionality built in — not rented from a dozen monthly subscriptions that never quite talk to each other.' },
  ],
};

export const LDS_GLANCE = {
  kicker: '02 — At a glance',
  title: 'The platform in numbers',
  note: 'Editable placeholders — swap in live figures any time.',
  tiles: [
    { value: '400+', label: 'Products & variants', editable: true },
    { value: '155', label: 'Countries shipped to' },
    { value: '4', label: 'Languages (EN · FR · ES · IT)' },
    { value: '30+', label: 'Automated email templates' },
    { value: '20+', label: 'Connected integrations' },
  ],
};

export const LDS_SECTIONS = [
  {
    id: 'storefront',
    num: '03',
    kicker: '03 — The storefront experience',
    title: 'A fast, mobile-first shop with a backstage feel',
    why:
      'Most visitors arrive on a phone, so the shop has to feel quick, premium and effortless to buy from — and keep working long after the sale.',
    blocks: [
      {
        icon: 'Store',
        heading: 'Browse & discover',
        points: [
          'Fast, mobile-first React storefront with a dark “backstage” aesthetic, brand typography and a fly-to-cart micro-animation — no flash of unstyled content.',
          '“Shop Here” mega menu with category thumbnails and seasonal tiles (e.g. gift vouchers), plus an admin menu health check that flags broken links.',
          'Product pages with colour/size variants (per-variant images and stock), markdown descriptions, pack-savings badges and delivery estimates.',
          'Back-in-stock alerts for guests and account holders, wishlist and one-tap product sharing.',
        ],
      },
      {
        icon: 'CreditCard',
        heading: 'Buy with zero friction',
        points: [
          'Basket drawer showing live progress towards free delivery and upgrade thresholds, with delivery estimates in the basket.',
          'Buy-now express (Apple Pay / Google Pay) straight from the product page.',
          'Checkout as a guest or signed in — whatever is fastest for the customer.',
        ],
      },
      {
        icon: 'Truck',
        heading: 'A checkout that just works',
        points: [
          'Postcode-first UK address lookup (postcode + house number → full address) plus Google-powered autocomplete for any country.',
          'Saved address book with a checkout dropdown and a “remembered address” on re-order; type-to-search country picker across 155 destinations.',
          'Live shipping options, discount and gift-voucher codes, and Express Checkout (Apple/Google Pay), Stripe cards and PayPal.',
          'Public Order Status page and a live delivery tracking timeline (ShipTheory + carrier tracking) with out-for-delivery alerts.',
        ],
      },
      {
        icon: 'Star',
        heading: 'Content & social proof',
        points: [
          'Reviews with photos and moderation, a homepage review-photo strip and a “Customer Wall” slider.',
          'A Journal (blog) with categories, a formatting toolbar, auto-generated buying guides, cover images and social sharing.',
          'Instagram strip and connected socials to keep drummers engaged beyond checkout.',
        ],
      },
      {
        icon: 'ShieldCheck',
        heading: 'Trust, safety & plumbing',
        points: [
          'Cloudflare Turnstile on public forms, GDPR-friendly one-click unsubscribe, and a “test copy” guard so staging can never take real payments.',
          'Site-wide SEO, sitemap, Google Search Console, GA4, Meta Pixel, a Google Shopping / Merchant Center feed and canonical domain handling.',
          'A site translation layer (EN / FR / ES / IT) — hybrid AI translation with nightly pre-warm and rate-limit guardrails.',
        ],
      },
    ],
    shots: [
      { caption: 'The Shop — browse drumsticks, accessories & merch', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/99e60kym_image.png' },
      { caption: 'Product page — variants, pack sizes & Beat Tokens earned', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/lwefg37p_image.png' },
      { caption: 'Basket drawer — live progress to free delivery & upgrades', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/8dvzgtr8_image.png' },
      { caption: 'Checkout — postcode-first address capture & Express Pay', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/yr7gk28r_image.png' },
    ],
    callouts: [
      {
        tag: 'Clever bit · Address capture',
        quote:
          'Type a UK postcode and house number and the full address drops in instantly — anywhere else in the world, Google autocomplete takes over. Fewer keystrokes, fewer failed deliveries.',
        cite: 'Postcode-first address capture',
      },
      {
        tag: 'Clever bit · Conversion',
        quote:
          'The mobile Express-Pay “guest gate” is A/B-tested, and the platform automatically keeps the winning variant — so the fastest path to purchase is always live.',
        cite: 'Express Pay A/B test with automatic winner',
      },
    ],
  },

  {
    id: 'builder',
    num: '04',
    kicker: '04 — Custom Stick Builder',
    title: 'Made-to-order sticks, configured and priced live',
    why:
      'Custom sticks are the brand’s signature — the builder turns a genuinely complex spec into something any drummer can design, see and price in seconds.',
    blocks: [
      {
        icon: 'Palette',
        heading: 'Design it step by step',
        points: [
          'Choose model/profile (including “Classic Models” with blurbs), wood and tip shape — six tip styles shown with imagery, plus a blended-tip option.',
          'Pick finish/paint with transparent surcharges, opt into B-stock builds, and add laser engraving or a logo upload with a live mock-up preview.',
        ],
      },
      {
        icon: 'Ruler',
        heading: 'Precise to the player',
        points: [
          'Dimension sliders in 0.05" increments, direct numeric entry, and an inch ↔ millimetre toggle.',
          'Every spec is captured exactly as the drummer intends — no “close enough”.',
        ],
      },
      {
        icon: 'Calculator',
        heading: 'Priced in real time',
        points: [
          'A live price breakdown driven by an admin-managed pricing matrix.',
          'Pack sizes and pack weights feed directly into the shipping engine, so quotes are accurate end to end.',
        ],
      },
      {
        icon: 'SplitSquareHorizontal',
        heading: 'Split fulfilment, automatically',
        points: [
          'Stock items go to the warehouse via ShipTheory immediately.',
          'The custom build drops onto the workshop Trello board, whose columns drive the customer’s status emails.',
          'When the sticks are made, one click — “custom sticks shipped” — closes the loop.',
        ],
      },
    ],
    shots: [
      { caption: 'Custom Stick Builder — model, wood, tip, millimetre sizing & live spec sheet', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/qt7gty9b_image.png' },
      { caption: 'Finishes, grips & artwork with a live, VAT-correct price breakdown', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/oms7rfpt_image.png' },
    ],
    callouts: [
      {
        tag: 'Clever bit · Operations',
        quote:
          'A single order can split in two — stock out of the warehouse today, the custom build onto the workshop board — and the customer’s emails update automatically as the Trello card moves.',
        cite: 'Split fulfilment: warehouse + workshop as one order',
      },
    ],
  },

  {
    id: 'loyalty',
    num: '05',
    kicker: '05 — Loyalty, growth and lifecycle',
    title: 'Turning first orders into lifelong regulars',
    why:
      'Repeat business is where the margin lives — so retention, rewards and lifecycle marketing are built in and run themselves.',
    blocks: [
      {
        icon: 'Coins',
        heading: 'Beat Tokens loyalty',
        points: [
          '1 token per £1, with tiered money-off applied right at checkout and a bonus for photo reviews.',
          'Pending-token nudges, a live balance in the account “Backstage Pass”, and legacy old-site orders imported so long-time customers keep their history.',
        ],
      },
      {
        icon: 'TrendingUp',
        heading: 'Cart Upgrades',
        points: [
          'When the basket crosses a spend threshold, an “Upgrade your cart” offer presents a product at a configurable % off RRP with Add / Skip.',
          'Accepted upgrades are real paid lines — VAT, weight and stock all handled correctly.',
          'Admin sets the tiers and % off; an analytics panel shows accept-vs-skip rates and upgrade revenue per tier.',
        ],
      },
      {
        icon: 'Gift',
        heading: 'Gift Vouchers',
        points: [
          'Custom-value vouchers auto-emailed on a chosen date, with an A4 printable voucher sheet and a live balance checker.',
          'Multi-voucher redemption across the whole store (including the custom builder), 30-day expiry nudges and admin issue/adjust.',
        ],
      },
      {
        icon: 'Tag',
        heading: 'Discounts manager',
        points: [
          'Percentage or fixed codes, single or multi-use, expiry and product-scope rules, with reservation safeguards.',
          'Win-back and abandoned-basket rescue codes, each with full send logs.',
        ],
      },
      {
        icon: 'Mail',
        heading: 'Transactional email suite (30+ templates)',
        points: [
          'Welcome, order confirmation, payment link, dispatch & tracking, out-for-delivery, delivered, review request (10 days after delivery), back-in-stock, voucher delivery & nudges, locker alerts, newsletter welcome, win-back, abandoned basket — plus team alerts.',
          'Resend delivery with webhook status (delivered / bounced) tied back to the order, branded short links and a same-domain image proxy for deliverability.',
          'Preview and send-test for every template.',
        ],
      },
      {
        icon: 'Send',
        heading: 'Campaigns & team digests',
        points: [
          'Email campaigns with a two-step send, send log, subscriber CRM and one-click unsubscribe.',
          'Weekly sales snapshot, a Monday workshop nudge, a monthly round-up and stock-forecast emails straight to the team.',
        ],
      },
    ],
    shots: [
      { caption: 'Beat Tokens — earn, track & redeem from the Backstage Pass', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/eitxzygd_image.png' },
      { caption: 'Cart Upgrade — an unlocked offer at 50% off, Add or Skip', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/4c91xyf8_image.png' },
      'Upgrade analytics — accept vs skip & revenue per tier (admin)',
    ],
    callouts: [
      {
        tag: 'Clever bit · Rewards',
        quote:
          'Beat Tokens aren’t a bolt-on — they’re earned on every pound, boosted for photo reviews, spent at checkout, and even back-filled from the old site so loyal customers never lost their history.',
        cite: 'Beat Tokens loyalty engine',
      },
      {
        tag: 'Clever bit · Revenue',
        quote:
          'Cross a spend threshold and the basket offers a genuine upgrade at a set discount — Add or Skip. Every choice is logged, so the team can see exactly which tiers earn and which get skipped.',
        cite: 'Cart upgrades with accept/skip analytics',
      },
    ],
  },

  {
    id: 'artists',
    num: '06',
    kicker: '06 — Accounts & the Artist programme',
    title: 'Accounts for everyone — VIP treatment for artists',
    why:
      'Endorsed artists are both customers and marketing. The platform treats them like VIPs while stripping out the admin that used to come with them.',
    blocks: [
      {
        icon: 'UserCircle',
        heading: 'The customer “Backstage Pass”',
        points: [
          'Orders and live status, one-tap re-order, Beat Tokens, address book, wishlist and back-in-stock alerts.',
          'A personal File Locker and email preferences, with Google sign-in or email/password.',
        ],
      },
      {
        icon: 'Crown',
        heading: 'Endorsed Artist portal',
        points: [
          'A dashboard with signature-stick models, standing orders with safe automatic charges and pack ordering at artist pricing.',
          'Addresses auto-filled from the roster (Airtable) and a two-way File Locker for mock-ups.',
          'The team drops a design → the artist gets a one-tap email link → they approve, request changes or comment in a thread → the team replies straight from the CRM.',
          'An art-files modal at checkout lets artists attach designs to signature-pack orders.',
        ],
      },
      {
        icon: 'Users',
        heading: 'Public Artists pages',
        points: [
          'A roster grid, a featured-artist slider and individual artist pages.',
          '“Hire Our Artists” — a session-drummer directory with a world map, shareable map cards and “Book me” cards.',
          'Endorsement applications synced to Airtable.',
        ],
      },
    ],
    shots: [
      { caption: 'The Backstage Pass — profile, saved addresses, tokens & file locker', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/ezn8cc05_image.png' },
      { caption: 'Personal File Locker — drop artwork, scans & mock-ups (PNG/JPG/SVG/PDF)', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/7i296pip_image.png' },
      { caption: '“Hire Our Artists” — the session-drummer directory on a live world map', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/76b4eeak_image.png' },
    ],
    callouts: [
      {
        tag: 'Clever bit · Collaboration',
        quote:
          'Signature-stick artwork goes back and forth in one place: the team drops a mock-up, the artist taps an email link to approve or comment, and the whole thread lives inside the CRM — no lost inbox chains.',
        cite: 'Artist file-locker approval threads',
      },
    ],
  },

  {
    id: 'international',
    num: '07',
    kicker: '07 — International by default',
    title: 'Sell worldwide with the true landed cost',
    why:
      'A huge share of drummers aren’t in the UK — so the store quotes the real duty, tax and shipping up front, and there are no nasty surprises at the door.',
    blocks: [
      {
        icon: 'Globe',
        heading: 'Reach & live rates',
        points: [
          'Ships to 155 destinations with a shipping-zone engine (UK, EU, ROW) and pack-weight scaling.',
          'Live Printful rates for merch and ShipTheory services for everything else.',
        ],
      },
      {
        icon: 'Receipt',
        heading: 'Import duty & tax estimator',
        points: [
          'Per-country VAT/GST, duty by product category, de-minimis thresholds, handling fees and live FX — for every destination.',
          'US tariffs with DDP collection and a tariff-watch alert when the rules change.',
          'Admin can edit any country’s rules.',
        ],
      },
      {
        icon: 'Landmark',
        heading: 'Correct VAT, correct paperwork',
        points: [
          'UK orders VAT-inclusive, export orders ex-VAT, and vouchers kept outside scope until they’re redeemed.',
          'Sender EORI and customs data sent to the courier on every international shipment.',
        ],
      },
    ],
    shots: [
      { caption: 'Checkout — a full landed-cost estimate (duty, VAT, handling & FX) for the destination', src: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_brand-ascent-1/artifacts/s9azmq8v_image.png' },
      'Admin — per-country duty, VAT & tariff rules editor with live tariff-watch',
    ],
    callouts: [
      {
        tag: 'Clever bit · Cross-border',
        quote:
          'Pick any of 155 countries and the checkout shows the real landed cost — VAT or GST, duty by category, de-minimis, handling and live FX — with DDP collection on US tariffs and an alert the moment the rules move.',
        cite: 'Import-duty estimator for 155 countries',
      },
    ],
  },

  {
    id: 'backoffice',
    num: '08',
    kicker: '08 — The back-office',
    title: 'One login that actually runs the business',
    why:
      'This is where the SaaS sprawl died: orders, fulfilment, products, customers, support and analytics all live behind one admin — the shop runs the operation, not just the storefront.',
    blocks: [
      {
        icon: 'LayoutDashboard',
        heading: 'Dashboard',
        points: [
          'Workshop queue, payment-health alerts and an integration-keys check at a glance.',
          'Dark/light mode and a genuinely mobile-friendly admin.',
        ],
      },
      {
        icon: 'ClipboardList',
        heading: 'Orders',
        points: [
          'Filterable, searchable list with CSV export and an expandable detail (items, customer, click-to-copy address, mailto links).',
          'Order financials: goods, discounts, upgrades, VAT, Stripe fee, net received and tokens issued.',
          'Customer notes (optionally emailed), manual orders (phone/email with stock linking, address auto-fill and branded pay links), cancel & restock, fulfilment retry, alt-address resend and legacy order import.',
        ],
      },
      {
        icon: 'Package',
        heading: 'Fulfilment',
        points: [
          'ShipTheory booking per order — automatic on payment, with re-send, tracking poll and status sync.',
          'Printful for print-on-demand merch, Trello for the workshop and split fulfilment across both.',
          'Carrier tracking via TrackingMore / Royal Mail.',
        ],
      },
      {
        icon: 'Boxes',
        heading: 'Products',
        points: [
          'Editor with variants and variant images, a pack stock pool and inline stock edits with instant low-stock alerts.',
          '“Live in shop” toggle with scheduled go-live and a launch announcement.',
          'Cart-upgrade tiers, the custom pricing matrix and the Google Shopping feed.',
        ],
      },
      {
        icon: 'Users',
        heading: 'CRM',
        points: [
          'Customers (accounts and guests) with a full timeline — orders, emails, support, tokens, reviews, notes and locker activity.',
          'Quick actions (log a call / dated note), subscribers CRUD, artists with portal management and lockers, the endorsement pipeline and Airtable sync.',
        ],
      },
      {
        icon: 'Headphones',
        heading: 'Support zone',
        points: [
          'A unified inbox for contact forms and email (Hostinger agentic mail) with conversational threading and a rich-text composer.',
          'Drag-and-drop closure, 7-day auto-close nudges and a morning digest.',
          'Support Autopilot — AI-drafted replies with an approval queue or full autopilot — plus a bug-report chatbot.',
        ],
      },
      {
        icon: 'Bot',
        heading: 'AI assistants',
        points: [
          'A storefront AI assistant for product Q&A with suggested questions.',
          'An admin AI “trading analyst” that reads journey and sales data.',
        ],
      },
      {
        icon: 'BarChart3',
        heading: 'Analytics',
        points: [
          'Sales analytics and a customer-journey funnel (device breakdown, abandoned baskets with rescue emails, A/B gate results).',
          'Upgrade analytics, a GA4 traffic dashboard and a Search Console panel.',
        ],
      },
      {
        icon: 'SlidersHorizontal',
        heading: 'Controls & settings',
        points: [
          'Team roles (owner / admin / team) and shipping settings (zones, free-delivery rules, sender & EORI).',
          'Import-duty rules editor, menu health check and a feedback inbox.',
          'Emails admin: template previews/tests, campaigns, send log and branded short links with click counts.',
        ],
      },
    ],
    shots: [
      'Admin dashboard — workshop queue & payment health',
      'Order detail — financials & fulfilment',
      'Support inbox — threaded with Autopilot drafts',
    ],
    callouts: [
      {
        tag: 'Clever bit · Support',
        quote:
          'Support Autopilot drafts replies from the full customer timeline — the team can approve from a queue, or let it run fully automatically — so a small team answers like a big one.',
        cite: 'Support Autopilot',
      },
    ],
  },
];

export const LDS_INTEGRATIONS = {
  kicker: '09 — Integrations map',
  title: 'Every service, wired into one platform',
  why:
    'Instead of a dozen disconnected subscriptions, every external service is connected into the same system — so data flows cleanly end to end.',
  items: [
    { name: 'Stripe', line: 'Cards, Apple Pay, Google Pay, webhooks & payment links' },
    { name: 'PayPal', line: 'Alternative express checkout' },
    { name: 'Resend', line: 'Transactional email with delivery webhooks' },
    { name: 'ShipTheory', line: 'Courier booking, tracking poll & status sync' },
    { name: 'Printful', line: 'Print-on-demand merch with live rates' },
    { name: 'Trello', line: 'Workshop board driving custom-build status' },
    { name: 'Airtable', line: 'Artist roster & endorsement pipeline sync' },
    { name: 'Google Places', line: 'Address autocomplete & geocoding' },
    { name: 'Google Analytics 4', line: 'Traffic & behaviour dashboard' },
    { name: 'Search Console', line: 'Indexing & search performance panel' },
    { name: 'Merchant Center', line: 'Google Shopping product feed' },
    { name: 'Meta Pixel', line: 'Conversion tracking & remarketing' },
    { name: 'Cloudflare Turnstile', line: 'Bot protection on public forms' },
    { name: 'TrackingMore', line: 'Carrier tracking incl. Royal Mail' },
    { name: 'Hostinger Mail', line: 'Agentic mail for the support inbox' },
    { name: 'Instagram', line: 'Live social strip on the storefront' },
    { name: 'Frankfurter FX', line: 'Live exchange rates for duty & tax' },
    { name: 'Claude (Emergent)', line: 'AI drafting, translation & analysis' },
    { name: 'Object storage', line: 'Emergent storage for files & images' },
  ],
};

export const LDS_ENGINEERING = {
  kicker: '10 — Engineering & quality',
  title: 'Built like a product, not a project',
  why:
    'The platform is engineered to stay fast, safe and easy to evolve — so new features ship without the whole thing wobbling.',
  blocks: [
    {
      icon: 'Code2',
      heading: 'Modern, maintainable stack',
      points: [
        'React (Tailwind, shadcn) frontend with a FastAPI + MongoDB backend.',
        'Background crons for nudges, digests, forecasts and translation pre-warm.',
      ],
    },
    {
      icon: 'ShieldCheck',
      heading: 'Security by design',
      points: [
        'Security audits with remediation — CSRF guard, owner-locked uploads, rate limits and signed URLs.',
        'A staging/live payment guard and proper secret management.',
      ],
    },
    {
      icon: 'FlaskConical',
      heading: 'Continuous QA',
      points: [
        'Automated backend scripts and browser regression suites run after every feature.',
        'Incident write-ups turned into permanent safeguards.',
      ],
    },
    {
      icon: 'Server',
      heading: 'Reliable delivery',
      points: [
        'Deployed on Emergent with a custom domain and canonical www.',
        'Zero-downtime republish, so updates never take the shop offline.',
      ],
    },
  ],
};

export const LDS_RESULTS = {
  kicker: '11 — Results',
  title: 'One system, running the whole business',
  lead:
    'Spreadsheets, inboxes and manual courier bookings replaced by a single platform the team runs end to end — storefront, workshop, fulfilment, loyalty, artists and support in one place.',
  note: 'Editable results block — drop in live figures and outcomes whenever you’re ready.',
  stats: [
    { value: '155', label: 'Countries served', editable: true },
    { value: '4', label: 'Store languages', editable: true },
    { value: '30+', label: 'Automated emails', editable: true },
    { value: '20+', label: 'Integrations in one system', editable: true },
  ],
  outcomes: [
    'A mobile-first storefront with Express-Pay, loyalty and rescue flows built for conversion.',
    'A custom stick builder that prices and fulfils made-to-order sticks automatically.',
    'A back-office that replaced the SaaS sprawl — orders, fulfilment, CRM, support and analytics in one login.',
  ],
};

export const LDS_CTA = {
  title: 'Got a business that’s outgrown its tools?',
  sub: 'We build platforms that run the whole operation — not just the shop window. Let’s talk.',
  email: 'team@boostmarketing.agency',
  siteUrl: 'https://boostmarketing.agency/#contact',
};
